/**
 * SweepPathRenderer — renders a wide glowing corridor sweep path on the hangar floor.
 *
 * Visual style: flat ribbon (y≈0.05m) with semi-transparent fill + bright dashed center markers.
 * Matches the prototype-1 "taxiway" look.
 *
 * Requires `layerService` to call `markSceneDirty()` after adding/removing geometry,
 * because Mapbox's custom layer only re-renders on demand.
 */

import * as THREE from 'three'
import { FEET_TO_METERS } from '@/constants/units'
import type { MapboxCustomLayerService } from '@/services/3d/MapboxCustomLayerService'
import type { TowStep } from '../data/towingPlan'

const PATH_SAMPLES = 80
const CORRIDOR_HALF_WIDTH = 4      // meters (~13ft) — half-width of the green corridor
const DASH_HALF_WIDTH = 0.35       // meters — half-width of each dash mark
const DASH_HALF_LENGTH = 1.8       // meters — half-length of each dash mark
const DASH_EVERY = 6               // sample interval between dashes
const FLOOR_Y = 0.05               // meters above floor for corridor
const DASH_Y = 0.08                // meters above floor for dashes (slightly above corridor)

export class SweepPathRenderer {
  private scene: THREE.Scene
  private layerService: MapboxCustomLayerService
  private group: THREE.Group

  constructor(scene: THREE.Scene, layerService: MapboxCustomLayerService) {
    this.scene = scene
    this.layerService = layerService
    this.group = new THREE.Group()
    this.group.name = 'sweep-path-renderer'
    scene.add(this.group)
  }

  show(step: TowStep): void {
    this.clear()

    const [p0, cp1, cp2, p3] = step.sweepPath

    // Sample cubic Bezier into world-space points (meters, y = floor level)
    const points: THREE.Vector3[] = []
    for (let i = 0; i <= PATH_SAMPLES; i++) {
      const t = i / PATH_SAMPLES
      points.push(new THREE.Vector3(
        cubicBezier(p0[0], cp1[0], cp2[0], p3[0], t) * FEET_TO_METERS,
        FLOOR_Y,
        cubicBezier(p0[2], cp1[2], cp2[2], p3[2], t) * FEET_TO_METERS,
      ))
    }

    const color = new THREE.Color(step.pathColor)

    // ── Corridor ribbon (wide, semi-transparent) ────────────────────────────
    this.group.add(this.buildRibbon(points, CORRIDOR_HALF_WIDTH, FLOOR_Y, color, 0.4))

    // ── Dashed center markers (bright, narrow) ─────────────────────────────
    const dashColor = lightenColor(step.pathColor, 0.55)
    const dashMat = new THREE.MeshBasicMaterial({
      color: dashColor,
      transparent: true,
      opacity: 0.9,
      side: THREE.DoubleSide,
      depthWrite: false,
    })

    for (let i = DASH_EVERY; i < points.length - 1; i += DASH_EVERY * 2) {
      const dash = this.buildDash(points, i, DASH_HALF_WIDTH, DASH_HALF_LENGTH, DASH_Y, dashMat)
      if (dash) this.group.add(dash)
    }

    this.layerService.markSceneDirty()
  }

  hide(): void {
    this.clear()
    this.layerService.markSceneDirty()
  }

  dispose(): void {
    this.clear()
    this.scene.remove(this.group)
  }

  // ── Private helpers ─────────────────────────────────────────────────────────

  /** Build a flat quad-strip ribbon along `points` at the given half-width and y. */
  private buildRibbon(
    points: THREE.Vector3[],
    halfWidth: number,
    y: number,
    color: THREE.Color,
    opacity: number,
  ): THREE.Mesh {
    const positions: number[] = []
    const indices: number[] = []

    for (let i = 0; i < points.length; i++) {
      const pt = points[i]
      const perp = this.perpAt(points, i)

      const lx = pt.x + perp.x * halfWidth
      const lz = pt.z + perp.z * halfWidth
      const rx = pt.x - perp.x * halfWidth
      const rz = pt.z - perp.z * halfWidth

      positions.push(lx, y, lz, rx, y, rz)

      if (i > 0) {
        const b = (i - 1) * 2
        indices.push(b, b + 1, b + 2, b + 1, b + 3, b + 2)
      }
    }

    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
    geo.setIndex(indices)

    const mat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity,
      side: THREE.DoubleSide,
      depthWrite: false,
    })

    return new THREE.Mesh(geo, mat)
  }

  /** Build a single dash rectangle at sample index `i`. */
  private buildDash(
    points: THREE.Vector3[],
    i: number,
    halfW: number,
    halfL: number,
    y: number,
    material: THREE.MeshBasicMaterial,
  ): THREE.Mesh | null {
    if (i < 0 || i >= points.length) return null
    const pt = points[i]
    const fwd = this.fwdAt(points, i)
    const perp = new THREE.Vector3(-fwd.z, 0, fwd.x)

    // Four corners of the dash quad
    const v = [
      pt.x + perp.x * halfW + fwd.x * halfL, y, pt.z + perp.z * halfW + fwd.z * halfL,
      pt.x - perp.x * halfW + fwd.x * halfL, y, pt.z - perp.z * halfW + fwd.z * halfL,
      pt.x - perp.x * halfW - fwd.x * halfL, y, pt.z - perp.z * halfW - fwd.z * halfL,
      pt.x + perp.x * halfW - fwd.x * halfL, y, pt.z + perp.z * halfW - fwd.z * halfL,
    ]

    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.Float32BufferAttribute(v, 3))
    geo.setIndex([0, 1, 2, 0, 2, 3])
    return new THREE.Mesh(geo, material)
  }

  /** Perpendicular (XZ-plane, left-pointing) at sample index. */
  private perpAt(pts: THREE.Vector3[], i: number): THREE.Vector3 {
    const f = this.fwdAt(pts, i)
    return new THREE.Vector3(-f.z, 0, f.x)
  }

  /** Forward direction at sample index. */
  private fwdAt(pts: THREE.Vector3[], i: number): THREE.Vector3 {
    if (i < pts.length - 1) {
      return pts[i + 1].clone().sub(pts[i]).normalize()
    }
    return pts[i].clone().sub(pts[i - 1]).normalize()
  }

  private clear(): void {
    while (this.group.children.length > 0) {
      const child = this.group.children[0]
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose()
        if (!Array.isArray(child.material)) child.material.dispose()
      }
      this.group.remove(child)
    }
  }
}

// ── Utility ─────────────────────────────────────────────────────────────────

function cubicBezier(a: number, b: number, c: number, d: number, t: number): number {
  const mt = 1 - t
  return mt * mt * mt * a + 3 * mt * mt * t * b + 3 * mt * t * t * c + t * t * t * d
}

function lightenColor(hex: string, amount: number): number {
  const c = new THREE.Color(hex)
  c.lerp(new THREE.Color(0xffffff), amount)
  return c.getHex()
}
